/**
 * HeaderButtonsForm - Button overlay for Form pages
 * Layering architecture: sibling to HeaderContainer, both position: fixed
 *
 * USAGE:
 * // CORRECT - siblings, not wrapper:
 * <HeaderContainer />
 * <HeaderButtonsForm currentContext={currentContext} onBack={handleBack} headerTitle="..." />
 *
 * REQUIRED PROPS:
 * - currentContext: object with { name: 'Display Name' }
 * - onBack: function to handle back button click
 *
 * OPTIONAL PROPS:
 * - headerTitle: string to display instead of context name
 * - onCheckUnsaved: () => boolean | Promise<boolean>, asked before Back leaves — pass the form's
 *   useUnsavedChanges confirmLeave (#78). Back is always shown; it asks only when something changed.
 *
 * FEATURES:
 * Desktop: Title + back button + optional search (80px row)
 * Mobile: Back button + title only (50px row, single row)
 */

import React from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import useMediaQuery from '../../../hooks/useMediaQuery';
import StandardButton from '../StandardButton';
import AnimatedSearchBar from '../AnimatedSearchBar';

const HeaderButtonsForm = ({ currentContext, onBack, headerTitle, showGroupSearch = false, onCheckUnsaved = null }) => {
    const theme = useTheme();
    const { isMobile, sidebarWidth, isSidebarCompact } = useMediaQuery();
    const { t } = useTranslation('ui');

    // Use headerTitle if provided, otherwise fall back to context name
    const displayTitle = headerTitle || currentContext?.name || '';

    // Back is always shown. With unsaved changes it asks first, through onCheckUnsaved (#78,
    // addendum 1 item 4) — it used to be hidden whenever any field was filled.
    const showBackButton = true;

    // Calculate Back button position to align right edge with sidebar's right button
    // Sidebar padding: 16px (compact) or 20px (full)
    // Back button width is approximately 100px (icon + gap + text + padding)
    const sidebarPadding = isSidebarCompact ? 16 : 20;
    const backButtonWidth = 100; // Approximate width of "Back" pill button
    const backButtonLeft = sidebarWidth - sidebarPadding - backButtonWidth;

    // Determine text color based on currentContext prop
    const contextString = typeof currentContext === 'string' ? currentContext : null;
    const textColor = contextString === 'locality' ? '#024B99' :
                      contextString === 'groups' ? '#BB4D9A' :
                      theme?.primary || '#024B99';

    // Handle group search (dispatches event that GroupsGrid listens to)
    const handleGroupSearch = (searchTerm) => {
        console.log('🔍 Group search triggered:', searchTerm);
        window.dispatchEvent(new CustomEvent('ournet:search-groups', {
            detail: { searchTerm }
        }));
    };

    // Mobile: Single 50px row with back button + title
    if (isMobile) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '50px',
                zIndex: 101,
                pointerEvents: 'none'
            }}>
                {/* Title - centered in available space */}
                <div style={{
                    position: 'absolute',
                    left: '80px',
                    right: '100px',
                    top: 0,
                    height: '50px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 3
                }}>
                    <div style={{
                        color: textColor,
                        fontSize: '16px',
                        fontFamily: 'OpenSauceSans',
                        fontWeight: '700',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {displayTitle}
                    </div>
                </div>

                {/* Back Button - vertically centered in white content bar (4px grey + 46px white) */}
                {showBackButton && (
                    <div style={{ position: 'absolute', left: '80px', top: '11px', zIndex: 3, pointerEvents: 'auto' }}>
                        <StandardButton
                            variant="context"
                            iconName="ArrowLeft"
                            label={t('common:back')}
                            onClick={onBack}
                            onCheckUnsaved={onCheckUnsaved}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Desktop: 80px row with title + back + optional search
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '80px',
            zIndex: 101,
            pointerEvents: 'none'
        }}>
            {/* Title - centered on middle content column */}
            <div style={{
                position: 'absolute',
                left: '490px',
                right: '340px',
                top: '4px',
                height: '56px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3
            }}>
                <div style={{
                    color: textColor,
                    fontSize: '18px',
                    fontFamily: 'OpenSauceSans',
                    fontWeight: '700',
                    textAlign: 'center'
                }}>
                    {displayTitle}
                </div>
            </div>

            {/* Back Button - right edge aligns with sidebar's right button */}
            {showBackButton && (
                <div style={{ position: 'absolute', left: `${backButtonLeft}px`, top: '12px', zIndex: 3, pointerEvents: 'auto' }}>
                    <StandardButton
                        variant="context"
                        iconName="ArrowLeft"
                        label={t('common:back')}
                        onClick={onBack}
                        onCheckUnsaved={onCheckUnsaved}
                    />
                </div>
            )}

            {/* Animated Search Bar - Groups context only */}
            {showGroupSearch && (
                <div style={{ pointerEvents: 'auto' }}>
                    <AnimatedSearchBar
                        context="groups"
                        onSearch={handleGroupSearch}
                        position={{ right: '120px', top: '12px' }}
                        placeholder={t('search_groups') || 'Search groups...'}
                        zIndex={10001}
                    />
                </div>
            )}
        </div>
    );
};

export default HeaderButtonsForm;
